import * as React from 'react';
import { css } from '@emotion/core';
import { Str } from '../../localization/translationKeys';
import Msg from '../../localization/Msg';
import { COLORS } from '../../globalStyle/colors';
import { Routes } from '../../navigation/routes';
import { Link } from 'react-router-dom';
import BulletedMsg from '../../layout/BulletedMsg';
import { faCheckCircle, faHistory } from '@fortawesome/free-solid-svg-icons';
import { faCircle as faCircleHollow } from '@fortawesome/free-regular-svg-icons/faCircle';
import { faCircle as faCircleSolid } from '@fortawesome/free-solid-svg-icons/faCircle';
import { useAppState } from '../../core/redux/hooks';
import { useMemo } from 'react';


const styles = {
  root:          css`
  
  `,
  title:         css`
    display: block;
    font-size: 11px;
    color: ${COLORS.DARK_GREY};
    margin-bottom: 8px;
    text-transform: uppercase;
  `,
  courseworkLinks: css`
    border: 1px solid ${COLORS.LIGHTER_GREY};
    border-radius: 8px;
    
    & > * {
      border-bottom: 1px solid ${COLORS.LIGHTER_GREY};
    }
    
    & > *:last-child {
      border-bottom: none;
    }
  `,
  courseworkLink:  css`
    display: block;
    color: ${COLORS.MEDIUM_GREY};
    text-decoration: none;
    padding: 18px 15px;
     
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  linkIcon:      css`
    font-size: 15px;
  `,
  disabledLink:   css`
    opacity: .4;
    border-bottom: 1px solid ${COLORS.LIGHT_GREY};
  `,
  iconBlue:       css`
    font-size: 15px;
    color: ${COLORS.BLUE};
  `,
  iconGreen:      css`
    font-size: 15px;
    color: ${COLORS.GREEN};
  `,
  iconMediumGrey: css`
    font-size: 15px;
    color: ${COLORS.MEDIUM_GREY};
  `,
  iconDarkGrey:   css`
    font-size: 15px;
    color: ${COLORS.DARK_GREY};
  `,
};

const activeLink = (route: string) => (
  window.location.pathname.endsWith(route)
);

const iconFor = (
  nextStep: boolean,
  started: boolean,
  complete: boolean,
) => (
  complete
    ? faCheckCircle
    : nextStep
    ? faCircleSolid
    : started
      ? faHistory
      : faCircleHollow
);

const iconColorFor = (
  nextStep: boolean,
  started: boolean,
  complete: boolean,
  active: boolean,
) => (
  complete
    ? styles.iconBlue
    : nextStep || started
    ? styles.iconGreen
    : active
      ? styles.iconDarkGrey
      : styles.iconMediumGrey
);

type Props = {
  routes: Routes;
  className?: string;
};


/**
 * CourseworkMenu: a component that
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CourseworkMenu: React.FC<Props> = props => {
  const project   = useAppState(state => state.projectSummary);
  const routes  = props.routes;

  const availableAfter = project?.courseAvailableAfter ?? 0;

  const courseDisabled = useMemo(
    () => {
      if (availableAfter === 1)
        return !project?.followupSurveyCreated;
      else if (availableAfter === 2)
        return !project?.ratersCompleted;
      else if (availableAfter === 3)
        return !project?.selfSurveyCompleted;
      else if (availableAfter === 4)
        return !project?.setGoalsCompleted;
      else if (availableAfter === 5)
        return !project?.reportAvailable;
      else if (availableAfter === 6)
        return !project?.devPlanCompleted;;
    },
    [availableAfter, project],
  );

  /* memoize project settings */
  const courseNotStarted = useMemo(() => project?.courseCompletionPct === 0, [project]);
  const courseStarted    = useMemo(() => (
    project?.courseCompletionPct ?? 0) > 0 && (project?.courseCompletionPct ?? 0) < 100,
     [project],
  );
  const courseCompleted  = useMemo(() => project?.courseCompletionPct === 100, [project]);
  const courseHasCompletion  = useMemo(() => project?.courseCompletionPct! >= 0, [project]);

  const menuText  = useMemo(() => 
    !courseHasCompletion
      ? Str.CourseworkAccess
      : courseNotStarted
        ? Str.CourseworkBegin 
        : courseCompleted
          ? Str.CourseworkCompleted
          : Str.CourseworkContinue,
    [courseNotStarted, courseCompleted, courseHasCompletion],
  );

  return (
    <div css={styles.root} className={props.className}>
      <Msg str={Str.Coursework} css={styles.title} defaultMsg={'Coursework'} />
      <div css={styles.courseworkLinks}>
        <Link key='ECourse'
          to={courseDisabled ? '#' : props.routes.ECourse}
          css={[
                styles.courseworkLink,
                courseDisabled && styles.disabledLink,
              ]}
        >
          <BulletedMsg
            icon={
              iconFor(
                courseNotStarted!,
                courseStarted!,
                courseCompleted!,
              )
            }
            msg={menuText}
            iconStyle={
              iconColorFor(
                courseNotStarted!,
                courseStarted!,
                courseCompleted!,
                activeLink(routes.ECourse),
              )
            }
            spacePx={14}
          />          
        </Link>
      </div>
    </div>
  )
};

export default CourseworkMenu;
